// WHERE A REFUSAL TAKES THE PERSON (2026-09-29).
//
// The form has always said why a Submit did nothing in ONE place: a red sentence under the Submit
// button. On a phone the page is about 3,900px tall and the question it is talking about can be
// 2,500px above that sentence.
//
// The morning it cost us: the owner of Showdown Tattoo (Walnut, CA, a shop that does tattoos and
// piercings) opened his own form from the welcome screen, inside Instagram's browser, eleven
// minutes after signing up. He filled it in, signed, and pressed Submit SEVEN times in eight minutes
// (the form's own record: `form_refused`, reason `no_service`, 10:50am to 10:58am CDT). Each time
// the sentence under the button said "Tell us whether today is a tattoo or a piercing." and nothing
// near it could be chosen: the two choices sit under the address, most of a page above. He wrote to
// us, "It says let me know if it's a tattoo or piercing yet there is no option to choose", opened
// Safari, and signed there. Chris, forwarding it: "critical customer question".
//
// Measured over the 14 days before it: `no_service` refused 12 submits, and the other refusals that
// name a question somewhere else on the page refused far more (a health question 145, who to call
// 119 + 118 + 59 + 59, the ID photo 93, an unticked clause 81, the terms 60). Every one of them left
// the person to go and find the question.
//
// So a refusal now TAKES the person to what it is about: the page scrolls the question to the middle
// of the screen, a field that is typed in gets the cursor, and for the choice between a tattoo and a
// piercing the sentence is said again right under the two choices. The sentence under the Submit
// button stays where it has always been. Nothing about WHAT is refused changed, and nothing here
// can stop a signing: a refusal whose question cannot be found on the page scrolls nowhere and says
// what it always said.
//
// This file is the pure half: which element a refusal is about. Form.tsx calls `goToRefusal` from
// `refuse()`, and marks the two blocks that have no input of their own to find (the ID photo, the
// signature pad) with `data-refusal`.

/** The reasons `refuse()` is called with in Form.tsx whose question has ONE input to find by name. */
const BY_NAME: Record<string, string> = {
  no_service: "serviceType",
  no_payment_method: "paymentMethod",
  no_emergency_name: "emergencyContactName",
  no_emergency_relationship: "emergencyContactRelationship",
  no_emergency_phone: "emergencyContactPhone",
  no_emergency_address: "emergencyContactAddress",
  no_guardian_name: "guardianName",
  no_guardian_relationship: "guardianRelationship",
  no_second_parent_name: "secondParentName",
  // TW-0279 — a cosmetic tattoo's procedures (their block is marked `data-refusal`, found first) and what the visit is
  no_procedure: "cosmeticProcedures",
  no_cosmetic_visit: "cosmeticVisit",
  // TW-0279 slice 10 — whether a scalp signing covers scars, by the form's own check or the API's SCALP_SCARS_UNANSWERED (its
  // block is marked `data-refusal`, found first), and the photo of the doctor's instructions has its block marked alone
  scalp_scars_unanswered: "scalpCoversScars",
  // 2026-10-08 — a shop in HIPAA mode: the practice's Notice of Privacy Practices left unticked (hipaaForm.ts), by the form's
  // own check or the API's NPP_NOT_ACKNOWLEDGED; its block is marked `data-refusal`, found first
  privacy_notice_unticked: "privacy-notice",
  // TW-0488 (2026-10-26) — which ID it is and its number, where the shop's state's rule keeps them (idType.ts), by the form's own
  // check or the API's ID_TYPE_UNANSWERED / ID_NUMBER_UNANSWERED: the type's radios share a name, the number is one box
  no_id_type: "idType",
  no_id_number: "idNumber",
  // …and a phone number the door could not read (the API's PHONE_NOT_VALID): the door answers after the signature, at the
  // bottom of the page, and the box is near the top, so this is the refusal that most needs to take the person somewhere
  phone_not_valid: "phoneNumber",
  // TW-0503 (2026-10-29) — a birth date that makes no day, or no day yet (Form.tsx, the first gate after the choice of service
  // since the form audit): its first box, the date's own sentence under the three. The age box it shared with an age that
  // disagreed (`birth_date_disagrees`, 2026-10-26 to 10-29) is gone: the age is read off the birth date
  birth_date_not_valid: "birthDateMonth",
  // …and a client under 18 where the shop takes no minors, or its state is unknown (the form audit, docs/FORM-AUDIT-2026-10-26.md):
  // the same box, the 18+ sentence under the birth date
  minor_not_taken: "birthDateMonth",
  // TW-0501 — and three of the form's own whose question is one input, which took nobody anywhere until the door's minors
  // codes below wanted them as a place to go: the cover-up question, the ear question, and the other parent's way
  coverup_unanswered: "coveringExisting",
  ear_unanswered: "earPiercing",
  no_second_parent_choice: "secondParentChoice",
  // …and the door's answer about a minor's spot (MINOR_PLACEMENT_NO, MINOR_PLACEMENT_UNVERIFIABLE): the spot's own box
  door_minor_spot: "locationOnBody",
  // TW-0526, the client-form side (2026-10-29) — the parent's phone the door could not read (GUARDIAN_PHONE_NOT_VALID): their box
  guardian_phone_not_valid: "guardianPhone",
};

/** TW-0501 — EVERY OTHER DOOR CODE A PAGE CAN MEET, TO ITS BOX (Grace's note on the card, 2026-10-26: "map the code (and every
 *  other door code a page can meet: MINOR_SHOP_OFF, MINOR_ID_PHOTO_MISSING, PHONE_NOT_VALID) to its box, the way the form's own
 *  refusals are mapped since 5f214be1f"). The door answers after the signature, at the bottom of the page; its sentence was
 *  said under the Submit button and the client was left to find the age box, the parent's block or the photo themselves. The
 *  codes Form.tsx already follows by their own field (the health answers, the shop's own questions, a minor's paper, the ID's
 *  type and number, the phone, a box too long, the privacy notice) are not here; each code here names the refusal it is
 *  answered as. A code the door answers about a minor the page did not read as one (a page loaded before the form audit, a
 *  body the page did not gate) goes to the birth date when the block it names is not on the page: DOOR_FALLS_TO. */
const DOOR_REASONS: Record<string, string> = {
  GUARDIAN_MISSING: "door_guardian_missing",
  MINOR_SHOP_OFF: "door_minor_age",
  MINOR_NO_STATE: "door_minor_age",
  MINOR_STATE_NO: "door_minor_age",
  MINOR_COUNTY_NO: "door_minor_age",
  MINOR_UNDER_FLOOR: "door_minor_age",
  MINOR_COSMETIC: "door_minor_age",
  MINOR_REFUSED: "door_minor_age",
  MINOR_COVERUP_ONLY: "door_minor_coverup",
  MINOR_PLACEMENT_NO: "door_minor_spot",
  MINOR_PLACEMENT_UNVERIFIABLE: "door_minor_spot",
  EAR_PIERCING_UNANSWERED: "door_minor_ear",
  MINOR_ID_PHOTO_MISSING: "door_minor_id_photo",
  GUARDIAN_PROOF_MISSING: "door_guardian_proof",
  SECOND_PARENT_MISSING: "door_second_parent",
  GUARDIAN_CERTIFICATION_MISSING: "door_guardian_certification",
  // …and three the form asks itself first, which the door answers only to a page or a body that did not
  TERMS_NOT_ACCEPTED: "terms_unticked",
  NO_SIGNATURE: "no_signature",
  NO_ID_PHOTO: "no_license_photo",
  // 2026-10-31 — a lot photo the shop requires, missing from a body (White Lotus Body Arts, Ventura: "make it mandatory that
  // they cannot submit the form without doing the photo of the needle cartridge"); the form asks itself first (lotPhotos.ts)
  NEEDLE_PHOTO_MISSING: "no_needle_photo",
  INK_PHOTO_MISSING: "no_ink_photo",
};

/** The refusal Form.tsx answers a door code as, for the codes above, else undefined. */
export function doorReasonOf(code: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(DOOR_REASONS, code) ? DOOR_REASONS[code] : undefined;
}

/** A door reason's places, in order: the block its code names where the page drew it, else the birth date, where a minor the
 *  page did not read as one is decided (TW-0503: the age is read off it). Each is a reason this file already finds. */
const DOOR_FALLS_TO: Record<string, string[]> = {
  door_guardian_missing: ["no_guardian_name", "birth_date_not_valid"],
  door_minor_age: ["birth_date_not_valid"],
  door_minor_coverup: ["coverup_unanswered", "birth_date_not_valid"],
  door_minor_ear: ["ear_unanswered", "birth_date_not_valid"],
  door_minor_id_photo: ["no_minor_id_photo", "birth_date_not_valid"],
  door_guardian_proof: ["no_guardian_proof_photo", "birth_date_not_valid"],
  door_second_parent: ["no_second_parent_choice", "birth_date_not_valid"],
  door_guardian_certification: ["no_guardian_certification", "birth_date_not_valid"],
};

/** THE BOXES THAT CARRY NO NAME, ONLY AN ID (2026-10-26, the read of refusals at the signing door). The service's clauses
 *  and the terms box are drawn with `id="acknowledgment-and-waiver.<key>"` and no `name` (Form.tsx AcknowledgmentItem), and a
 *  state notice the client ticks with `id="state-notice.<id>"` (NoticeItem), so the name these three were looked for by
 *  matched nothing on the real page and the refusal took nobody anywhere: this file's own test drew a box WITH the name.
 *  532 refusals of an unticked clause in the 30 days to 2026-10-26, at 49 shops, one visit pressing Submit 48 times. */
const BY_ID: Record<string, (detail: string) => string> = {
  acknowledgment_unticked: (detail) => (detail ? `acknowledgment-and-waiver.${detail}` : ""),
  terms_unticked: () => "acknowledgment-and-waiver.termsAndConditions",
  notice_unticked: (detail) => (detail ? `state-notice.${detail}` : ""),
};

/** The reasons whose question is said by the caller: WHICH health question, WHICH clause. */
const BY_DETAIL: Record<string, (detail: string) => string> = {
  health_question: (detail) => detail,
  // 2026-10-01 — a health question left blank (healthQuestions.ts): its radios, by the questionnaire key they post under
  health_unanswered: (detail) => detail,
  // 2026-10-01 — a yes whose details box was left empty (healthQuestions.ts DETAILS_BOX): the box, by the name it posts under
  health_details: (detail) => detail,
  // TW-0279 — an answer the shop's state bars a cosmetic tattoo on (cosmetic.ts cosmeticAnswerRefusal): its radios
  cosmetic_answer_refused: (detail) => detail,
  // 2026-10-02 — one of the shop's own clauses left unticked (ownClauses.ts): its box, by the name it carries since today
  own_clause_unticked: (detail) => `own-clause.${detail}`,
  // TW-0412 — one of the shop's own questions left without a yes or a no (ownQuestions.ts), by the form's own check or the
  // API's OWN_QUESTION_UNANSWERED: its radios, by the name they share
  own_question_unanswered: (detail) => `own-question.${detail}`,
  // TW-0275 (2026-10-14) — a box the API's signing door found longer than it takes (FORM_FIELD_TOO_LONG): the box, by the
  // name it posts under. The box's own maxLength stops a client's typing first, so this is for a value the page was handed
  // (a returning client's details filled in from an older record)
  field_too_long: (detail) => detail,
  // TW-0541 (2026-10-29) — THE BROWSER'S OWN STOP (NATIVE_STOPS below): the box, by the name Form.tsx's `invalid` listener read
  // off it, so the page can take the client there and say what to do in its own words where the browser says nothing.
  // Measured on 2 Ton Gallery's form at the iPhone profile (390x844): a Submit pressed at the bottom with the artist box
  // empty lands the page on the box with the browser's focus ring and, in WebKit, no message of any kind; 391 of the 432
  // presses the browser stopped in the 7 days to 2026-10-29 were iPhones, 5 presses a visit, one visit 51.
  native_missing: (detail) => detail,
  native_mismatch: (detail) => detail,
  native_range: (detail) => detail,
  native_other: (detail) => detail,
  // TW-0526, the client-form side (2026-10-29) — a box a state's rule requires that no yes or no carries, left empty, by the form's
  // own check or the API's STATE_FIELD_UNANSWERED: the box, by its body key, in its part of the page (Form.tsx StateFieldBox)
  you_field_unanswered: (detail) => detail,
  health_field_unanswered: (detail) => detail,
  guardian_field_unanswered: (detail) => detail,
};

/** The reasons with SEVERAL marked blocks on one page, the caller saying which: each block is marked with the reason and the
 *  detail joined by a dot, and that block is looked for before anything else. TW-0270 slice 5 (2026-10-16): a paper the state's
 *  rule keeps with a minor's record (minors.ts papersAsked), by the paper's id, because one signing can ask for two (a Texas
 *  piercing: the notarized consent and the affidavit), each in a block of its own with no input to find by name. */
const BLOCK_BY_DETAIL: Record<string, (detail: string) => string> = {
  no_paper_photo: (detail) => `no_paper_photo.${detail}`,
  // TW-0526, the client-form side (2026-10-29) — a statement the rule has the parent tick after its first (Illinois's piercing rule
  // three), by the statement's id: each in a block of its own; the first's block is the reason's own, found next
  no_guardian_certification: (detail) => `no_guardian_certification.${detail}`,
};

/** A name as a CSS attribute value: quotes and backslashes escaped, nothing else touched. */
function quoted(name: string): string {
  return `"${name.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** The selectors to try, in order, for one refusal. Empty when the refusal is about no one place on
 *  the page (a minor this state does not allow, a shop that could not be read), which is not an
 *  error: the sentence under the button is the whole of what those say. */
export function refusalSelectors(reason: string, detail = ""): string[] {
  // a door reason is its places' selectors, in their order (DOOR_FALLS_TO)
  if (DOOR_FALLS_TO[reason]) return DOOR_FALLS_TO[reason].flatMap((place) => refusalSelectors(place, detail));
  const out: string[] = [];
  // the one block of several the caller named comes first (BLOCK_BY_DETAIL: which paper)
  if (BLOCK_BY_DETAIL[reason] && detail) out.push(`[data-refusal~=${quoted(BLOCK_BY_DETAIL[reason](detail))}]`);
  // a block marked for the refusal comes next: it is the whole question (its label and every
  // choice), where an input found by name is one radio of two
  if (reason) out.push(`[data-refusal~=${quoted(reason)}]`);
  // a box that carries only an id (BY_ID: the clauses, the terms, a state notice)
  const id = BY_ID[reason] ? BY_ID[reason](detail) : "";
  if (id) out.push(`[id=${quoted(id)}]`);
  const named = BY_DETAIL[reason] && detail ? BY_DETAIL[reason](detail) : BY_NAME[reason];
  if (named) out.push(`[name=${quoted(named)}]`);
  return out;
}

// ── WHAT THE BEACON SAYS OF A REFUSAL (2026-10-26; the read is `./run.sh refusals`) ───────────────────────────────────
// Chris, after a shop's "their ID is not valid" was traced by hand to the form's own words and the phone box: "we should
// be beaconing refusals and seeing if we have defects." A `form_refused` row said WHICH refusal and nothing else, so 532
// refusals of an unticked clause could not say which clause. These two say which box and where on the page, from this
// file's own tables, so the beacon can never be handed anything but our own words (beacon.ts checks both again).

/** THE SECTIONS OF THE PAGE, top to bottom, and `door` for a refusal the API's signing door answered after the press. The
 *  beacon's `step`; the server's form_beacon.py STEPS is the same list, held to this one by its test. */
export const REFUSAL_STEPS = ["shop", "you", "emergency", "service", "health", "clauses", "id", "minor", "signature", "door"] as const;
export type RefusalStep = (typeof REFUSAL_STEPS)[number];

/** Which section each of Form.tsx's own `refuse("…")` codes is about. Form.test.tsx holds every call site to a line here. */
const STEP_OF: Record<string, RefusalStep> = {
  no_shop: "shop",
  // TW-0505 — the shop's state's rules still on their way at the press, or never come (stateRules.ts): the page's own half of
  // the shop, like no_shop. A failure takes the client to the line at the top of the form (`data-refusal`, Form.tsx)
  state_rules_loading: "shop",
  state_rules_failed: "shop",
  birth_date_not_valid: "you",
  minor_not_taken: "you",
  no_emergency_name: "emergency",
  no_emergency_relationship: "emergency",
  no_emergency_phone: "emergency",
  no_emergency_address: "emergency",
  no_service: "service",
  no_artist: "service",
  no_procedure: "service",
  no_cosmetic_visit: "service",
  ear_unanswered: "service",
  no_payment_method: "service",
  health_question: "health",
  cosmetic_answer_refused: "health",
  health_unanswered: "health",
  health_details: "health",
  own_question_unanswered: "health",
  scalp_scars_unanswered: "health",
  acknowledgment_unticked: "clauses",
  notice_unticked: "clauses",
  own_clause_unticked: "clauses",
  cash_preferred_unticked: "clauses",
  privacy_notice_unticked: "clauses",
  terms_unticked: "clauses",
  no_id_type: "id",
  no_id_number: "id",
  no_license_photo: "id",
  no_minor_id_photo: "minor",
  no_guardian_proof_photo: "minor",
  no_paper_photo: "minor",
  coverup_unanswered: "minor",
  no_guardian_name: "minor",
  no_guardian_relationship: "minor",
  no_guardian_certification: "minor",
  // TW-0526, the client-form side (2026-10-29) — a box a state's rule requires, by the part of the page it sits in
  you_field_unanswered: "you",
  health_field_unanswered: "health",
  guardian_field_unanswered: "minor",
  no_guardian_signature: "minor",
  no_second_parent_choice: "minor",
  no_second_parent_name: "minor",
  no_second_parent_signature: "minor",
  no_second_parent_photo: "minor",
  minor_or_placement_refused: "minor",
  no_signature: "signature",
  // 2026-10-31 — the two lot photos a shop requires sit in the last section, under the signature pad (lotPhotos.ts)
  no_needle_photo: "signature",
  no_ink_photo: "signature",
};

/** The section a refusal is about, or undefined for a code this file has not learned (the beacon then carries none). */
export function refusalStep(reason: string): RefusalStep | undefined {
  return STEP_OF[reason];
}

/** WHICH BOX OR QUESTION, as the beacon carries it: the caller's detail (the question's key, the clause's, the paper's id,
 *  the shop's own clause or question id), else the one input this file finds the refusal by, else nothing. Never a value:
 *  every detail Form.tsx passes is a key of ours, and beacon.ts drops anything not in a key's shape. */
export function refusalField(reason: string, detail = ""): string | undefined {
  if (detail) return detail;
  if (reason === "terms_unticked") return "termsAndConditions";
  return BY_NAME[reason] || undefined;
}

// ── THE BROWSER'S OWN STOP (TW-0501) ──────────────────────────────────────────────────────────────────────────────────────
// The name, the birth date, the phone, the email and the address are `required`, and the email is `type="email"`, so
// the browser itself stops a Submit with one of them empty or malformed, in the BROWSER's language ("Please fill out this
// field"), before the form's own code runs. The form audit (docs/FORM-AUDIT-2026-10-26.md § 4): not one of 6,723 signed rows
// has a blank name, email or address, so it works; and no row recorded it, so 858 visits started the form and never pressed
// Submit as far as the beacon could see, some of whom pressed it. Form.tsx listens for the form's `invalid` events and beacons
// the first of each press as a `form_refused` row: its `reason` one of these, its `field` the box, its `step` the box's section.

/** The kind of stop, off the box's own ValidityState (`native_missing` is "Please fill out this field"). */
export const NATIVE_STOPS = ["native_missing", "native_mismatch", "native_range", "native_other"] as const;
export type NativeStop = (typeof NATIVE_STOPS)[number];

/** Is this reason one of the browser's own stops? (Form.tsx marks the box and says its sentence for these too.) */
export function isNativeStop(reason: string): reason is NativeStop {
  return (NATIVE_STOPS as readonly string[]).includes(reason);
}

export function nativeStop(validity: Partial<ValidityState> | null | undefined): NativeStop {
  if (validity?.valueMissing) return "native_missing";
  if (validity?.typeMismatch || validity?.patternMismatch) return "native_mismatch";
  if (validity?.rangeUnderflow || validity?.rangeOverflow || validity?.stepMismatch) return "native_range";
  return "native_other";
}

/** The boxes of each section the browser can stop a Submit at, by name, top to bottom (Form.tsx). */
const BOX_STEP: Record<string, RefusalStep> = {
  firstName: "you", lastName: "you", birthDateMonth: "you", birthDateDay: "you", birthDateYear: "you",
  phoneNumber: "you", email: "you", address1: "you", address2: "you", city: "you", state: "you", zip: "you",
  serviceType: "service", artistChoice: "service", nameOfArtist: "service", cosmeticProcedures: "service", cosmeticVisit: "service",
  tattooDescription: "service", locationOnBody: "service", earPiercing: "service", jewelryMaterial: "service",
  jewelryNotes: "service", price: "service", priceKind: "service", paymentMethod: "service",
  medicalHistoryDetails: "health", medicationAndBloodThinners: "health", scalpCoversScars: "health",
  physicianName: "health", physicianPhone: "health", physicianAddress: "health",
  termsAndConditions: "clauses", hearFromShop: "clauses", shopMarketing: "clauses",
  emancipatedMinor: "minor", coveringExisting: "minor", guardianName: "minor", guardianRelationship: "minor",
  guardianCertification: "minor", secondParentChoice: "minor", secondParentName: "minor",
  idType: "id", idNumber: "id",
  signedDate: "signature",
  // TW-0526, the client-form side (2026-10-29) — the boxes a state's rule names (stateForms.ts stateFieldsFor), each in its place
  sexOnId: "you", lastAteAt: "health", guardianPhone: "minor",
  // TW-0629 — Florida's race on a piercing, beside the birth date
  race: "you",
};

/** The section a box the browser stopped a Submit at sits in, by its name or id: `healthQuestions` are the questionnaire's own
 *  keys (Form.tsx hands them in), a details box and one of the shop's own questions are health too, a clause, a notice or one
 *  of the shop's own clauses is a clause. Undefined for a box this does not know, and the row then carries no step. */
export function stepOfBox(name: string, healthQuestions: readonly string[] = []): RefusalStep | undefined {
  if (Object.prototype.hasOwnProperty.call(BOX_STEP, name)) return BOX_STEP[name];
  if (name.startsWith("emergencyContact")) return "emergency";
  if (healthQuestions.includes(name) || /Details$/.test(name) || name.startsWith("own-question.")) return "health";
  if (/^(acknowledgment-and-waiver|state-notice|own-clause)\./.test(name) || name === "privacy-notice") return "clauses";
  return undefined;
}

type Finder = Pick<Document, "querySelector">;

/** The element a refusal is about, or null. A selector the browser will not parse finds nothing. */
export function refusalTarget(reason: string, detail = "", doc: Finder | null = typeof document === "undefined" ? null : document): Element | null {
  if (!doc) return null;
  for (const selector of refusalSelectors(reason, detail)) {
    try {
      const found = doc.querySelector(selector);
      if (found) return found;
    } catch {
      /* not a selector this browser reads: try the next one */
    }
  }
  return null;
}

/** Is this something a person types into? Those get the cursor; a radio, a box or a button does not
 *  (on a phone a focused radio shows nothing, and the scroll has already brought it to them). */
export function takesTyping(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  if (tag !== "input") return false;
  const type = (el.getAttribute("type") || "text").toLowerCase();
  return !["radio", "checkbox", "button", "submit", "file", "hidden", "range", "color"].includes(type);
}

/** Take the person to the question. True when there was somewhere to take them. Never throws: a
 *  browser with no scrollIntoView (jsdom, an old webview) is a page that stays where it is. */
export function goToRefusal(reason: string, detail = "", doc: Finder | null = typeof document === "undefined" ? null : document): boolean {
  try {
    const el = refusalTarget(reason, detail, doc);
    if (!el) return false;
    // a radio or a box is inside its label, and the label is what the person reads: bring THAT to the middle
    const shown = (el.closest?.("label") as Element | null) ?? el;
    (shown as HTMLElement).scrollIntoView?.({ block: "center", behavior: "smooth" });
    if (takesTyping(el)) (el as HTMLElement).focus?.({ preventScroll: true });
    return true;
  } catch {
    return false;
  }
}
